import React from 'react';
import { KeyRound, LogOut, Plus, RefreshCw, Save, ShieldCheck, UserCheck, UserX } from 'lucide-react';
import { useTelegram } from '../hooks/useTelegram';

const ADMIN_KEY_STORAGE = 'jpexgo_admin_key_v1';
const STAFF_SESSION_STORAGE = 'jpexgo_staff_session_v1';
const ROLES = [
  {
    value: 'admin',
    label: 'Админ',
    description: 'Полный доступ: сотрудники и роли, кассиры, импорт Excel, отчёты, сверка с банком и служебные действия.',
  },
  {
    value: 'manager',
    label: 'Менеджер',
    description: 'Рабочий доступ: отчёты, сверка с банком, управление доставками и треки от клиентов. Не управляет сотрудниками.',
  },
  {
    value: 'operator',
    label: 'Оператор',
    description: 'Будущий модуль обработки треков: регистрация, редактирование, QR-наклейки и работа только по выбранным странам.',
  },
];
const COUNTRIES = ['KG', 'KZ', 'UZ'];

const getApiPath = (path) => {
  if (!path || typeof path !== 'string') return path;
  if (!path.startsWith('/api/')) return path;
  const apiBase = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/+$/, '');
  return apiBase ? `${apiBase}${path}` : path;
};

const apiPost = async (path, body) => {
  const res = await fetch(getApiPath(path), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body || {}),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Request failed: ${res.status}`);
  return data;
};

const formatDate = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const ActionButton = ({ children, onClick, disabled, tone = 'default' }) => {
  const tones = {
    default: 'border-stroke bg-white/5 text-text-primary hover:bg-white/10',
    yellow: 'border-neon-yellow/30 bg-neon-yellow/10 text-neon-yellow hover:bg-neon-yellow/15',
    red: 'border-red-400/30 bg-red-400/10 text-red-200 hover:bg-red-400/15',
    green: 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200 hover:bg-emerald-400/15',
  };
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex h-9 items-center justify-center gap-2 rounded-xl border px-3 text-xs font-semibold transition disabled:opacity-50 ${tones[tone] || tones.default}`}
    >
      {children}
    </button>
  );
};

const AdminLogin = ({ value, password, onChange, onPasswordChange, onSubmit, error, loading }) => (
  <div className="min-h-screen bg-bg px-4 py-8 text-text-primary">
    <div className="mx-auto flex min-h-[70vh] max-w-md items-center">
      <div className="w-full rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
        <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
          <ShieldCheck size={15} /> Админка
        </div>
        <h1 className="mt-3 text-2xl font-black">Вход для администратора</h1>
        <p className="mt-1 text-sm text-text-secondary">
          Введите логин и пароль администратора. Для первого входа можно использовать ADMIN_KEY.
        </p>
        <input
          value={value}
          onChange={(e) => onChange(e.target.value.trim())}
          placeholder="Логин или ADMIN_KEY"
          className="mt-5 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        <input
          value={password}
          onChange={(e) => onPasswordChange(e.target.value)}
          placeholder="Пароль администратора"
          type="password"
          className="mt-3 h-12 w-full rounded-2xl border border-stroke bg-[#161616] px-4 text-sm outline-none focus:border-neon-yellow/50"
        />
        {error ? <div className="mt-3 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        <button
          type="button"
          onClick={onSubmit}
          disabled={!value || loading}
          className="mt-4 h-12 w-full rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60"
        >
          Войти
        </button>
      </div>
    </div>
  </div>
);

const toggleCountryValue = (items, value) => (
  items.includes(value) ? items.filter((item) => item !== value) : [...items, value]
);

const roleLabel = (value) => ROLES.find((role) => role.value === value)?.label || value;
const roleDescription = (value) => ROLES.find((role) => role.value === value)?.description || '';

const AdminStaffPage = () => {
  const { initData } = useTelegram();
  const initDataRaw = initData?.initData || '';
  const [adminKey, setAdminKey] = React.useState(() => {
    try {
      return localStorage.getItem(ADMIN_KEY_STORAGE) || '';
    } catch (_) {
      return '';
    }
  });
  const [staffSessionToken, setStaffSessionToken] = React.useState(() => {
    try {
      const raw = localStorage.getItem(STAFF_SESSION_STORAGE) || '';
      if (!raw) return '';
      try {
        return JSON.parse(raw)?.staffSessionToken || '';
      } catch (_) {
        return raw;
      }
    } catch (_) {
      return '';
    }
  });
  const [loginKey, setLoginKey] = React.useState(adminKey);
  const [loginPassword, setLoginPassword] = React.useState('');
  const [staffUsers, setStaffUsers] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [actionLoading, setActionLoading] = React.useState('');
  const [error, setError] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [createdPassword, setCreatedPassword] = React.useState('');
  const [form, setForm] = React.useState({
    login: '',
    fullName: '',
    password: '',
    roles: ['operator'],
    countryScope: ['KG', 'KZ', 'UZ'],
  });

  const hasTelegramAuth = typeof initDataRaw === 'string' && initDataRaw.includes('hash=');
  const hasAuth = Boolean(hasTelegramAuth || adminKey || staffSessionToken);
  const authBody = React.useCallback((extra = {}) => {
    if (staffSessionToken) return { staffSessionToken, ...extra };
    if (hasTelegramAuth) return { initData: initDataRaw, ...extra };
    return { adminKey, ...extra };
  }, [adminKey, hasTelegramAuth, initDataRaw, staffSessionToken]);

  const loadStaff = React.useCallback(async () => {
    if (!hasAuth) return;
    setLoading(true);
    setError('');
    try {
      const r = await apiPost('/api/admin/staff/list', authBody());
      setStaffUsers(Array.isArray(r?.staffUsers) ? r.staffUsers : []);
    } catch (e) {
      setError(e?.message || 'Не удалось загрузить сотрудников');
      if (!hasTelegramAuth && !staffSessionToken) {
        try {
          localStorage.removeItem(ADMIN_KEY_STORAGE);
        } catch (_) {
          // ignore
        }
        setAdminKey('');
      }
      if (staffSessionToken) {
        try {
          localStorage.removeItem(STAFF_SESSION_STORAGE);
        } catch (_) {
          // ignore
        }
        setStaffSessionToken('');
      }
    } finally {
      setLoading(false);
    }
  }, [authBody, hasAuth, hasTelegramAuth, staffSessionToken]);

  React.useEffect(() => {
    loadStaff();
  }, [loadStaff]);

  const login = async () => {
    const nextKey = String(loginKey || '').trim();
    if (!nextKey) return;
    setLoading(true);
    setError('');
    try {
      let auth = { adminKey: nextKey };
      if (loginPassword) {
        const session = await apiPost('/api/staff/login', { login: nextKey, password: loginPassword });
        auth = { staffSessionToken: session.staffSessionToken };
        localStorage.setItem(STAFF_SESSION_STORAGE, JSON.stringify(session));
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        setStaffSessionToken(session.staffSessionToken || '');
      } else {
        localStorage.setItem(ADMIN_KEY_STORAGE, nextKey);
      }
      const r = await apiPost('/api/admin/staff/list', auth);
      setStaffUsers(Array.isArray(r?.staffUsers) ? r.staffUsers : []);
      setAdminKey(loginPassword ? '' : nextKey);
      setLoginPassword('');
    } catch (_) {
      try {
        localStorage.removeItem(ADMIN_KEY_STORAGE);
        localStorage.removeItem(STAFF_SESSION_STORAGE);
      } catch (__) {
        // ignore
      }
      setAdminKey('');
      setStaffSessionToken('');
      setError('Неверный логин, пароль или ключ администратора');
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    try {
      localStorage.removeItem(ADMIN_KEY_STORAGE);
      localStorage.removeItem(STAFF_SESSION_STORAGE);
    } catch (_) {
      // ignore
    }
    setAdminKey('');
    setStaffSessionToken('');
    setLoginKey('');
    setLoginPassword('');
    setStaffUsers([]);
  };

  const createStaff = async () => {
    if (!form.roles.length) {
      setError('Выберите роль сотрудника');
      return;
    }
    if (!form.countryScope.length) {
      setError('Выберите хотя бы одну страну доступа');
      return;
    }
    setActionLoading('create');
    setError('');
    setMessage('');
    setCreatedPassword('');
    try {
      const r = await apiPost('/api/admin/staff/create', authBody(form));
      setForm({ login: '', fullName: '', password: '', roles: ['operator'], countryScope: ['KG', 'KZ', 'UZ'] });
      setCreatedPassword(r?.password || '');
      setMessage(`Сотрудник создан: ${r?.staffUser?.fullName || ''}`);
      await loadStaff();
    } catch (e) {
      setError(e?.message || 'Не удалось создать сотрудника');
    } finally {
      setActionLoading('');
    }
  };

  const updateStaff = async (staff) => {
    if (!Array.isArray(staff.roles) || !staff.roles.length) {
      setError('Выберите роль сотрудника');
      return;
    }
    if (!Array.isArray(staff.countryScope) || !staff.countryScope.length) {
      setError('Выберите хотя бы одну страну доступа');
      return;
    }
    setActionLoading(`update:${staff.id}`);
    setError('');
    setMessage('');
    try {
      await apiPost('/api/admin/staff/update', authBody({
        id: staff.id,
        login: staff.login,
        fullName: staff.fullName,
        roles: staff.roles,
        countryScope: staff.countryScope,
        isActive: staff.isActive,
      }));
      setMessage(`Сотрудник обновлён: ${staff.fullName}`);
      await loadStaff();
    } catch (e) {
      setError(e?.message || 'Не удалось обновить сотрудника');
    } finally {
      setActionLoading('');
    }
  };

  const setActive = async (staff, isActive) => {
    if (!window.confirm(`${isActive ? 'Включить' : 'Отключить'} сотрудника ${staff.fullName}?`)) return;
    setActionLoading(`active:${staff.id}`);
    setError('');
    try {
      await apiPost('/api/admin/staff/set-active', authBody({ id: staff.id, isActive }));
      await loadStaff();
    } catch (e) {
      setError(e?.message || 'Не удалось изменить статус сотрудника');
    } finally {
      setActionLoading('');
    }
  };

  const resetPassword = async (staff) => {
    if (!window.confirm(`Сбросить пароль сотрудника ${staff.fullName}? Активные сессии будут закрыты.`)) return;
    setActionLoading(`password:${staff.id}`);
    setError('');
    setCreatedPassword('');
    try {
      const r = await apiPost('/api/admin/staff/reset-password', authBody({ id: staff.id }));
      setCreatedPassword(r?.password || '');
      setMessage(`Новый пароль для ${staff.fullName}`);
      await loadStaff();
    } catch (e) {
      setError(e?.message || 'Не удалось сбросить пароль');
    } finally {
      setActionLoading('');
    }
  };

  const patchStaff = (id, patch) => {
    setStaffUsers((items) => items.map((item) => (String(item.id) === String(id) ? { ...item, ...patch } : item)));
  };

  const selectedFormRole = form.roles[0] || '';

  if (!hasAuth) {
    return <AdminLogin value={loginKey} password={loginPassword} onChange={setLoginKey} onPasswordChange={setLoginPassword} onSubmit={login} error={error} loading={loading} />;
  }

  return (
    <div className="min-h-screen bg-bg px-4 py-5 text-text-primary">
      <div className="mx-auto max-w-6xl">
        <div className="rounded-3xl border border-stroke bg-surface p-5 shadow-[0_18px_80px_rgba(0,0,0,0.35)]">
          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full border border-neon-yellow/25 bg-neon-yellow/10 px-3 py-1 text-xs font-semibold text-neon-yellow">
                <ShieldCheck size={15} /> Роли и доступ
              </div>
              <h1 className="mt-3 text-2xl font-black tracking-tight">Сотрудники</h1>
              <p className="mt-1 max-w-2xl text-sm text-text-secondary">
                Единая авторизация для админов, менеджеров и будущего операторского модуля с QR-наклейками.
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <ActionButton onClick={loadStaff} disabled={loading} tone="yellow">
                <RefreshCw size={15} className={loading ? 'animate-spin' : ''} /> Обновить
              </ActionButton>
              {!hasTelegramAuth && !staffSessionToken ? (
                <ActionButton onClick={logout} disabled={loading}>
                  <LogOut size={15} /> Выйти
                </ActionButton>
              ) : null}
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-stroke bg-black/10 p-4">
            <div className="text-sm font-semibold">Добавить сотрудника</div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <input value={form.login} onChange={(e) => setForm((f) => ({ ...f, login: e.target.value }))} placeholder="Логин" className="h-11 rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
              <input value={form.fullName} onChange={(e) => setForm((f) => ({ ...f, fullName: e.target.value }))} placeholder="ФИО" className="h-11 rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
              <input value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} placeholder="Пароль или пусто для автогенерации" className="h-11 rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
              <button type="button" onClick={createStaff} disabled={actionLoading === 'create' || form.login.trim().length < 3 || form.fullName.trim().length < 2 || !form.roles.length || !form.countryScope.length} className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-neon-yellow px-4 text-sm font-bold text-black disabled:opacity-60">
                <Plus size={16} /> Добавить
              </button>
            </div>
            <div className="mt-3 grid gap-3 md:grid-cols-2">
              <div>
                <div className="mb-2 text-xs text-text-secondary">Роль сотрудника</div>
                <div className="flex flex-wrap gap-2">
                  {ROLES.map((role) => (
                    <button key={role.value} type="button" onClick={() => setForm((f) => ({ ...f, roles: [role.value] }))} className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.roles.includes(role.value) ? 'border-neon-yellow/40 bg-neon-yellow text-black' : 'border-stroke bg-white/5 text-text-secondary'}`}>
                      {role.label}
                    </button>
                  ))}
                </div>
                <div className="mt-2 rounded-xl border border-stroke bg-white/5 px-3 py-2 text-xs text-text-secondary">
                  <div className="font-semibold text-text-primary">{selectedFormRole ? `Права роли “${roleLabel(selectedFormRole)}”` : 'Выберите роль'}</div>
                  <div className="mt-1">{selectedFormRole ? roleDescription(selectedFormRole) : 'После выбора роли здесь будет показано, какие разделы получит сотрудник.'}</div>
                  <div className="mt-1 text-[11px] text-text-muted">У одного сотрудника выбирается одна основная роль. Так меньше ошибок с доступами.</div>
                </div>
              </div>
              <div>
                <div className="mb-2 text-xs text-text-secondary">Страны доступа</div>
                <div className="flex flex-wrap gap-2">
                  {COUNTRIES.map((country) => (
                    <button key={country} type="button" onClick={() => setForm((f) => ({ ...f, countryScope: toggleCountryValue(f.countryScope, country) }))} className={`rounded-full border px-3 py-1 text-xs font-semibold ${form.countryScope.includes(country) ? 'border-sky-400/40 bg-sky-400/15 text-sky-100' : 'border-stroke bg-white/5 text-text-secondary'}`}>
                      {country}
                    </button>
                  ))}
                </div>
                {!form.countryScope.length ? (
                  <div className="mt-2 rounded-xl border border-red-400/25 bg-red-400/10 px-3 py-2 text-xs text-red-100">
                    Выберите хотя бы одну страну, иначе сотрудник не будет понимать, с какими заказами работать.
                  </div>
                ) : (
                  <div className="mt-2 text-[11px] text-text-muted">Страны ограничат будущий операторский модуль и отчёты по странам.</div>
                )}
              </div>
            </div>
          </div>

          {createdPassword ? (
            <div className="mt-4 rounded-2xl border border-neon-yellow/30 bg-neon-yellow/10 p-4 text-sm text-neon-yellow">
              Пароль: <span className="font-mono text-xl font-black">{createdPassword}</span>. Показывается только один раз.
            </div>
          ) : null}
          {message ? <div className="mt-4 rounded-2xl border border-emerald-400/25 bg-emerald-400/10 p-3 text-sm text-emerald-100">{message}</div> : null}
          {error ? <div className="mt-4 rounded-2xl border border-red-400/25 bg-red-400/10 p-3 text-sm text-red-100">{error}</div> : null}
        </div>

        <div className="mt-4 grid gap-3">
          {staffUsers.map((staff) => (
            <div key={staff.id} className="rounded-3xl border border-stroke bg-surface p-4">
              <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <input value={staff.fullName || ''} onChange={(e) => patchStaff(staff.id, { fullName: e.target.value })} className="min-w-[220px] flex-1 rounded-2xl border border-stroke bg-[#161616] px-3 py-2 text-sm font-black outline-none focus:border-neon-yellow/50" />
                    <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold ${staff.isActive ? 'border-emerald-400/30 bg-emerald-400/10 text-emerald-200' : 'border-red-400/30 bg-red-400/10 text-red-200'}`}>
                      {staff.isActive ? 'Активен' : 'Отключён'}
                    </span>
                  </div>
                  <div className="mt-3 grid gap-2 md:grid-cols-[220px_1fr]">
                    <input value={staff.login || ''} onChange={(e) => patchStaff(staff.id, { login: e.target.value })} className="h-10 rounded-2xl border border-stroke bg-[#161616] px-3 text-sm outline-none focus:border-neon-yellow/50" />
                    <div className="flex flex-wrap gap-2">
                      {ROLES.map((role) => (
                        <button key={role.value} type="button" onClick={() => patchStaff(staff.id, { roles: [role.value] })} className={`rounded-full border px-3 py-1 text-xs font-semibold ${(staff.roles || []).includes(role.value) ? 'border-neon-yellow/40 bg-neon-yellow text-black' : 'border-stroke bg-white/5 text-text-secondary'}`}>
                          {role.label}
                        </button>
                      ))}
                      {(staff.roles || []).filter((role) => !ROLES.some((item) => item.value === role)).map((role) => (
                        <span key={role} className="rounded-full border border-stroke bg-white/5 px-3 py-1 text-xs font-semibold text-text-muted">
                          {roleLabel(role)}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="mt-2 rounded-xl border border-stroke bg-white/5 px-3 py-2 text-xs text-text-secondary">
                    <div className="font-semibold text-text-primary">Права: {roleLabel((staff.roles || [])[0] || '')}</div>
                    <div className="mt-1">{roleDescription((staff.roles || [])[0] || '') || 'Роль не выбрана. Сохранение заблокировано до выбора роли.'}</div>
                  </div>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {COUNTRIES.map((country) => (
                      <button key={country} type="button" onClick={() => patchStaff(staff.id, { countryScope: toggleCountryValue(staff.countryScope || [], country) })} className={`rounded-full border px-3 py-1 text-xs font-semibold ${(staff.countryScope || []).includes(country) ? 'border-sky-400/40 bg-sky-400/15 text-sky-100' : 'border-stroke bg-white/5 text-text-secondary'}`}>
                        {country}
                      </button>
                    ))}
                  </div>
                  {!(staff.countryScope || []).length ? (
                    <div className="mt-2 rounded-xl border border-red-400/25 bg-red-400/10 px-3 py-2 text-xs text-red-100">
                      У сотрудника не выбрана страна доступа. Сохранение заблокировано до выбора страны.
                    </div>
                  ) : null}
                  <div className="mt-3 grid gap-2 text-xs text-text-secondary md:grid-cols-3">
                    <div>Создан: {formatDate(staff.createdAt)}</div>
                    <div>Последний вход: {formatDate(staff.lastLoginAt)}</div>
                    <div>ID: {staff.id}</div>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2 lg:justify-end">
                  <ActionButton onClick={() => updateStaff(staff)} disabled={!!actionLoading}><Save size={15} /> Сохранить</ActionButton>
                  <ActionButton onClick={() => resetPassword(staff)} disabled={!!actionLoading} tone="yellow"><KeyRound size={15} /> Пароль</ActionButton>
                  <ActionButton onClick={() => setActive(staff, !staff.isActive)} disabled={!!actionLoading} tone={staff.isActive ? 'red' : 'green'}>
                    {staff.isActive ? <UserX size={15} /> : <UserCheck size={15} />}
                    {staff.isActive ? 'Отключить' : 'Включить'}
                  </ActionButton>
                </div>
              </div>
            </div>
          ))}
          {!loading && !staffUsers.length ? (
            <div className="rounded-3xl border border-stroke bg-surface p-6 text-center text-sm text-text-secondary">
              Сотрудников пока нет. Создайте первого администратора или оператора выше.
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default AdminStaffPage;
